import React, { forwardRef } from "react";
import styles from "./MedicalCenterResult.module.scss";
import { MedicalCenterResultType } from "types";
import SvgGenerator from "components/svgGenerator/SvgGenerator";
import Text from "components/text/Text";
import strings from "assets/strings";
import { settings } from "assets/settings";
import PhoneLink from "components/phoneLink/PhoneLink";

export const MedicalCenterResult = forwardRef(({ hit, objectType }: { hit: MedicalCenterResultType, objectType: any }, ref: any) => {
  const onNavigate = (hit: MedicalCenterResultType) => {
    const { city, street, houseNum } = hit;   
    const address = `${street || ''} ${houseNum || ''} ${city || ''}`;
    const encodedAddress = encodeURIComponent(address);
    const mobileUrl = `geo://0,0?q=${encodedAddress}`;
    const iosUrl = `maps://?q=${encodedAddress}`;
    const desktopUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    function isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }
    function isIOS() {
      return /iPhone|iPad|iPod/i.test(navigator.userAgent);
    }
    const url = isMobile() ? (isIOS() ? iosUrl : mobileUrl) : desktopUrl;
    window.open(url, "_blank");
  };

  const itemUrl = (hit: MedicalCenterResultType) => {
    switch (objectType) {
      case 4:
        return `${settings.medicalCenterPage}?medicalCenterTypeId=${hit.medicalCenterTypeId || ''}&branchNum=${hit.branchNum || ''}&subBranchNum=${hit.subBranchNum || ''}`;
      case 5:
        return `${settings.opticaPage}?medicalCenterTypeId=${hit.medicalCenterTypeId || ''}&branchNum=${hit.branchNum || ''}&subBranchNum=${hit.subBranchNum || ''}&serialNumber=${hit.serialNumber || ''}&serviceNameProviderBase64=${hit.serviceNameProviderBase64 || ''}&serviceTypeCode=35`;
      case 6:
        return `${settings.emergencyPage}?medicalCenterTypeId=${hit.medicalCenterTypeId || ''}&branchNum=${hit.branchNum || ''}&subBranchNum=${hit.subBranchNum || ''}&serialNumber=${hit.serialNumber || ''}&serviceNameProviderBase64=${hit.serviceNameProviderBase64 || ''}&serviceTypeCode=50`;
      case 7:
        return `${settings.pharmacyPage}?medicalCenterTypeId=${hit.medicalCenterTypeId || ''}&branchNum=${hit.branchNum || ''}&subBranchNum=${hit.subBranchNum || ''}&serialNumber=${hit.serialNumber || ''}&serviceNameProviderBase64=${hit.serviceNameProviderBase64 || ''}&serviceTypeCode=1`;
      case 8:
        return `${settings.womenEmergencyPage}?medicalCenterTypeId=${hit.medicalCenterTypeId || ''}&branchNum=${hit.branchNum || ''}&subBranchNum=${hit.subBranchNum || ''}&serialNumber=${hit.serialNumber || ''}&serviceNameProviderBase64=${hit.serviceNameProviderBase64 || ''}&serviceTypeCode=94`;
        case 9:
          return `${settings.dentalPage}?supplierCode=${encodeURIComponent(hit.supplierCode || '')}`;
    }
    return '';
  }

  return (
    <div className={styles.hitMedicalCenter} ref={ref}>
      <div className={styles.distanceSection}>
        {hit._rankingInfo?.geoDistance && (
          <Text className={styles.textTitle2} text={`${(hit._rankingInfo.geoDistance / 1000).toFixed(2)} ק"מ`} />
        )}
      </div>
      <div className={styles.medicalCenterDetails}>
        <div className={styles.section2}>
          {objectType == 5 && <SvgGenerator name="Icon" className={styles.avatar} objectName="medicalCenterSearchResults" fallBackIcon="Optica" />}
          {objectType == 6 && <SvgGenerator name="Icon" className={styles.avatar} objectName="medicalCenterSearchResults" fallBackIcon="Emergency" />}
          {objectType == 8 && <SvgGenerator name="Icon" className={styles.avatar} objectName="medicalCenterSearchResults" fallBackIcon="WomenEmergency" />}
          {objectType == 9 && <SvgGenerator name="Icon" className={styles.avatar} objectName="medicalCenterSearchResults" fallBackIcon="Teeth" />}
          {objectType == 4 && <SvgGenerator name="Icon" className={styles.avatar} objectName="medicalCenterSearchResults" fallBackIcon="MedicalCenter" />}
          <div className={styles.divText}>
            <a href={itemUrl(hit)} className={styles.textTitle}>
              {`${hit.name}`}
            </a>
            {(objectType == 7 && hit.inBranch) && (
              <span className={styles.span}>
                <SvgGenerator name="SmallLogo" objectName="medicalCenterSearchResults" />
                <Text
                  className={styles.textInBranch}
                  text={strings.medicalCentersSearchResults.inbranch}
                />
              </span>
            )}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.divText}>
            <Text
              className={styles.textTitle2}
              text={strings.globalStrings.address}
            />
            <span
              id={`navigateToAddress_${hit.objectID}`}
              className={styles.textAddress}>{`${hit.street || ''} ${hit.houseNum || ''}, ${hit.city || ''}`}
            </span>
            <span onClick={() => onNavigate(hit)} className={styles.spanClick} aria-describedby={`navigateToAddress_${hit.objectID}`} tabIndex={0} role="link">
              <SvgGenerator name="Location" className={styles.icon} objectName="medicalCenterSearchResults" />
              <Text
                className={styles.textNavigate}
                text={strings.medicalCentersSearchResults.navigateToAddress}
              />
            </span>
          </div>
        </div>
        {objectType == 9 && <div className={styles.section}>
          <div className={styles.divText2}>
            <Text className={styles.textTitle2} text={strings.medicalCentersSearchResults.servicesInClinic} />
            {hit.medicalServices && hit.medicalServices.map((service) => (<Text className={styles.textAddress} text={service} />))}
          </div>
        </div>
        }
        <div className={styles.section}>
          <div className={styles.divText2}>
            {(hit.phone1 || hit.phone2) && <Text
              className={styles.textTitle2}
              text={strings.medicalCentersSearchResults.callToOrder}
            />}
            {hit.phone1 && (
              <PhoneLink phoneNumber={String(hit.phone1)} />
            )}
            {hit.phone2 && (
              <PhoneLink phoneNumber={String(hit.phone2)} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
